import React, { useState, useEffect } from 'react';
import { ShieldCheck, RefreshCw, Save, Info } from 'lucide-react';
import userService from '../../services/user.service';
import { useAuth } from '../../hooks/useAuth';
import { AllRolesResponse, RolePermission } from '../../types';

// Role descriptions for display
const roleDescriptions: Record<string, string> = {
  ADMIN:         'Full system access — all modules, all operations',
  MD:            'Managing Director — read and approve across all modules',
  PM:            'Project Manager — manages projects, procurement, attendance, quality',
  SITE_ENG:      'Site Engineer — records attendance, progress, procurement requests',
  ACCOUNTS:      'Accounts — manages billing, payments, payroll',
  PURCHASE:      'Purchase Officer — manages materials and procurement',
  HR:            'HR Manager — manages employees and payroll',
  ESTIMATOR:     'Estimator — creates and manages project estimates',
  CUSTOMER:      'Customer Portal — view project progress and documents',
  SUPPLIER:      'Supplier Portal — manage dispatch plans and invoices',
  VENDOR:        'Vendor Portal — manage machinery and billing',
  SUBCONTRACTOR: 'Subcontractor Portal — view agreements, submit invoices',
};

// Permission columns
const permColumns = [
  { key: 'can_read',    label: 'Read',    color: 'text-blue-600' },
  { key: 'can_write',   label: 'Write',   color: 'text-green-600' },
  { key: 'can_delete',  label: 'Delete',  color: 'text-red-600' },
  { key: 'can_approve', label: 'Approve', color: 'text-purple-600' },
  { key: 'can_export',  label: 'Export',  color: 'text-yellow-600' },
];

const RolesPage = () => {
  const { hasRole }  = useAuth();
  const isAdmin      = hasRole('ADMIN');

  const [roles,    setRoles]    = useState<AllRolesResponse>({});
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState<string | null>(null);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [selected, setSelected] = useState<string>('');

  // Load all roles
  useEffect(() => {
    const load = async () => {
      try {
        const data = await userService.getAllRoles();
        setRoles(data);
        // Select first role by default
        const firstRole = Object.keys(data)[0];
        if (firstRole) setSelected(firstRole);
      } catch {
        setError('Failed to load roles.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Toggle a permission
  const handleToggle = async (
    role_code: string,
    module:    string,
    permKey:   string,
    current:   boolean
  ) => {
    if (!isAdmin) return;

    setSaving(`${role_code}-${module}-${permKey}`);
    try {
      await userService.updateRolePermissions(role_code, module, {
        [permKey]: !current
      });

      // Update local state
      setRoles(prev => ({
        ...prev,
        [role_code]: prev[role_code].map(p =>
          p.module === module
            ? { ...p, [permKey]: !current }
            : p
        )
      }));

      setSuccess('Permission updated.');
      setTimeout(() => setSuccess(''), 2000);
    } catch {
      setError('Failed to update permission.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(null);
    }
  };

  const roleList    = Object.keys(roles);
  const selectedPerms = selected ? (roles[selected] || []) : [];

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="page-container">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Roles & Permissions</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage module access for each role
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Info size={14} />
          {isAdmin
            ? 'Click checkboxes to toggle permissions'
            : 'View-only — ADMIN role required to edit'
          }
        </div>
      </div>

      {error   && <div className="alert-error mb-4"><span>{error}</span></div>}
      {success && <div className="alert-success mb-4"><span>{success}</span></div>}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* Left — Role list */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                <ShieldCheck size={16} />
                Roles ({roleList.length})
              </h3>
            </div>
            <div className="py-2">
              {roleList.map(role => (
                <button
                  key={role}
                  onClick={() => setSelected(role)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    selected === role
                      ? 'bg-blue-50 text-blue-700 font-medium border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <p className="font-medium">{role}</p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    {roles[role]?.length || 0} module{(roles[role]?.length || 0) !== 1 ? 's' : ''}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right — Permissions for selected role */}
        <div className="lg:col-span-3">
          {selected && (
            <div className="card">
              <div className="card-header">
                <div>
                  <h3 className="font-semibold text-gray-800">{selected}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {roleDescriptions[selected] || ''}
                  </p>
                </div>
                <span className="badge badge-blue text-xs">
                  {selectedPerms.length} module{selectedPerms.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Module</th>
                      {permColumns.map(col => (
                        <th key={col.key} className={`text-center ${col.color}`}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPerms.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-gray-400 py-8">
                          No module permissions configured
                        </td>
                      </tr>
                    ) : (
                      selectedPerms.map(perm => (
                        <tr key={perm.module}>
                          <td>
                            <span className="font-medium text-gray-700">
                              {perm.module}
                            </span>
                          </td>
                          {permColumns.map(col => {
                            const value   = perm[col.key as keyof RolePermission] as boolean;
                            const saveKey = `${selected}-${perm.module}-${col.key}`;
                            const isSaving = saving === saveKey;

                            return (
                              <td key={col.key} className="text-center">
                                {isSaving ? (
                                  <RefreshCw size={14}
                                    className="animate-spin mx-auto text-gray-400" />
                                ) : (
                                  <button
                                    onClick={() => handleToggle(
                                      selected, perm.module, col.key, value
                                    )}
                                    disabled={!isAdmin}
                                    className={`w-5 h-5 rounded border-2 mx-auto flex
                                                items-center justify-center transition-colors ${
                                      value
                                        ? 'bg-blue-600 border-blue-600'
                                        : 'border-gray-300 hover:border-gray-400'
                                    } ${!isAdmin ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                  >
                                    {value && (
                                      <svg className="w-3 h-3 text-white" fill="none"
                                        viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round"
                                          strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100
                              flex flex-wrap gap-4 text-xs text-gray-500">
                {permColumns.map(col => (
                  <span key={col.key} className={`flex items-center gap-1 ${col.color}`}>
                    <span className="font-medium">{col.label}:</span>
                    {col.key === 'can_read'    && 'View records'}
                    {col.key === 'can_write'   && 'Create and edit'}
                    {col.key === 'can_delete'  && 'Delete records'}
                    {col.key === 'can_approve' && 'Approve workflows'}
                    {col.key === 'can_export'  && 'Export to Excel/PDF'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolesPage;